import { useNavigate } from 'react-router-dom';
import { useLang } from '../../../hooks/useLang';
import { columnArticles } from '../../../mocks/column';

export default function ColumnBanner() {
  const navigate = useNavigate();
  const { t } = useLang();
  const featured = columnArticles.filter(a => a.featured).slice(0, 3);
  const categoryColors: Record<string, string> = {
    '政策深析': 'bg-[#F59E0B]/20 text-[#F59E0B]',
    '风险警示': 'bg-red-400/20 text-red-300',
    '行业动态': 'bg-[#A78BFA]/20 text-[#A78BFA]',
    '案例解析': 'bg-orange-400/20 text-orange-300',
    '实操指南': 'bg-blue-400/20 text-blue-300',
    '合规实务': 'bg-green-400/20 text-green-300',
  };
  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };
  return (
    <section className="py-16 bg-gradient-to-br from-[#1A0050] via-[#2E1065] to-[#1A0050] relative overflow-hidden">
      <div className="absolute inset-0 opacity-5" style={{backgroundImage:'radial-gradient(circle at 1px 1px,#fff 1px,transparent 0)',backgroundSize:'36px 36px'}}></div>
      <div className="absolute top-0 left-1/3 w-80 h-80 bg-[#7C3AED]/15 rounded-full blur-[120px]"></div>
      <div className="absolute bottom-0 right-1/4 w-56 h-56 bg-[#F59E0B]/8 rounded-full blur-[80px]"></div>
      <div className="relative max-w-6xl mx-auto px-4 lg:px-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-[#F59E0B]/20 rounded-lg flex items-center justify-center">
                <i className="ri-quill-pen-line text-[#F59E0B] text-base"></i>
              </div>
              <span className="text-[#F59E0B] text-xs font-bold tracking-widest uppercase">
                {t('站长独立观察专栏', "Editor's Column")}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white">
              清鉴<span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">纵论</span>
            </h2>
            <p className="text-white/50 text-sm mt-1 italic">ChaInsight Column · {t('独立视角，深度剖析', 'Independent perspective, deep analysis')}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-4 text-white/40 text-xs mr-4">
              <span><span className="text-white font-bold text-base">{columnArticles.length}</span> {t('篇深度文章','articles')}</span>
              <span><span className="text-white font-bold text-base">{(columnArticles.reduce((s,a)=>s+a.viewCount,0)/1000).toFixed(0)}k</span> {t('阅读','reads')}</span>
            </div>
            <button
              onClick={() => navigate('/column')}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#F59E0B] text-[#1A0050] font-black text-sm rounded-full hover:bg-[#FBBF24] transition-colors cursor-pointer whitespace-nowrap shadow-lg shadow-[#F59E0B]/25"
            >
              {t('进入专栏', 'Enter Column')} <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {featured.map((article, i) => (
            <article
              key={article.id}
              onClick={() => navigate(`/column/${article.id}`)}
              className="group relative bg-white/5 border border-white/10 hover:border-[#F59E0B]/30 rounded-2xl p-5 cursor-pointer transition-all hover:bg-white/8 overflow-hidden"
            >
              {i === 0 && <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#F59E0B] to-transparent"></div>}
              <div className="flex items-center justify-between mb-3">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${categoryColors[article.category] || 'bg-white/10 text-white/60'}`}>
                  {t(article.category, article.category)}
                </span>
                <span className="text-white/35 text-xs">{formatDate(article.publishedAt)}</span>
              </div>
              <h3 className="text-white font-bold text-base leading-snug mb-2 group-hover:text-[#F59E0B] transition-colors line-clamp-2">{article.title}</h3>
              <p className="text-white/45 text-xs italic mb-3 line-clamp-1">{article.subtitle}</p>
              <p className="text-white/55 text-sm leading-relaxed line-clamp-3">{article.abstract}</p>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/8">
                <div className="flex items-center gap-3 text-white/35 text-xs">
                  <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{(article.viewCount/1000).toFixed(1)}k</span>
                  <span className="flex items-center gap-1"><i className="ri-heart-line"></i>{article.likeCount}</span>
                  <span className="flex items-center gap-1"><i className="ri-time-line"></i>{article.readTime}min</span>
                </div>
                <span className="text-[#F59E0B] text-xs font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                  {t('阅读', 'Read')} <i className="ri-arrow-right-line"></i>
                </span>
              </div>
            </article>
          ))}
        </div>
        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-6 py-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#7C3AED] flex items-center justify-center text-white font-serif font-black text-sm">清</div>
            <p className="text-white/55 text-sm">{t('清鉴元衡 先生于成都 · Mr. ChaInsight，独立观察，持续深耕RWA合规研究', 'Mr. ChaInsight · Chengdu, independent observations on RWA compliance')}</p>
          </div>
        </div>
      </div>
    </section>
  );
}